// Geo helpers mirroring the mobile app's location.ts / geocoding.ts:
// haversine distances, human distance labels, field coordinates, and a
// Nominatim place lookup used when a search query looks like a place
// instead of a field name.

const earthRadiusMeters = 6371000;
const metersPerMile = 1609.344;

export function distanceInMeters(a, b) {
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const dLat = toRadians(b.latitude - a.latitude);
  const dLon = toRadians(b.longitude - a.longitude);
  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * earthRadiusMeters * Math.asin(Math.sqrt(h));
}

export function formatDistanceMiles(meters) {
  const miles = meters / metersPerMile;
  if (miles < 0.2) {
    return 'Right here';
  }
  const rounded = miles < 10 ? miles.toFixed(1) : Math.round(miles);
  return `${rounded} ${Number(rounded) === 1 ? 'mile' : 'miles'} away`;
}

export function coordinateForField(field) {
  const address = field?.address;
  if (
    address &&
    typeof address.latitude === 'number' &&
    typeof address.longitude === 'number'
  ) {
    return { latitude: address.latitude, longitude: address.longitude };
  }
  return undefined;
}

export function browserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Location is not available in this browser.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      error => reject(error),
      // A five-minute-old cached fix is plenty for "fields near me" and
      // avoids waiting on a fresh OS-level lookup.
      { enableHighAccuracy: false, maximumAge: 300000, timeout: 20000 },
    );
  });
}

/**
 * City-level location from the visitor's IP (geojs.io — free, keyless,
 * CORS-enabled). Used when precise browser geolocation fails, which on
 * desktop usually means OS Location Services is off for the browser.
 */
export async function approximateIpLocation() {
  const response = await fetch('https://get.geojs.io/v1/ip/geo.json', {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`IP location lookup failed (${response.status}).`);
  }

  const result = await response.json();
  const latitude = Number(result.latitude);
  const longitude = Number(result.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new Error('IP location lookup returned no coordinates.');
  }

  return { latitude, longitude };
}

export function geolocationFailureMessage(error) {
  // GeolocationPositionError codes: 1 = permission denied,
  // 2 = position unavailable, 3 = timeout.
  if (error?.code === 1) {
    return 'Location permission is blocked for this site. Allow location access in your browser and try again.';
  }

  if (error?.code === 2) {
    return "Your browser couldn't determine your location. On a Mac, check that Location Services is enabled for your browser in System Settings → Privacy & Security → Location Services.";
  }

  if (error?.code === 3) {
    return 'Finding your location took too long. Please try again.';
  }

  return 'We could not get your location. Check your browser location permissions and try again.';
}

/** Nominatim forward geocode (same fallback service the mobile app uses). */
export async function geocodePlace(query) {
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=us&q=${encodeURIComponent(query)}`;
  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Geocoding failed (${response.status}).`);
  }

  const results = await response.json();
  const first = Array.isArray(results) ? results[0] : undefined;
  if (!first) {
    return undefined;
  }

  return {
    latitude: Number(first.lat),
    longitude: Number(first.lon),
    label: first.display_name,
  };
}
